import { useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { RootState } from 'src/store';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';
import { navigateToCase } from '@cp/src/pages/CaseRecommendation/utils';
import StrategyNavigationHeader from '@cp/src/pages/CaseRecommendation/components/StrategyNavigationHeader';
import EndOfNavigationModal from '@cp/src/pages/CaseRecommendation/components/EndOfNavigationModal';
import {
  setShowEndOfNavigationModal,
  setStrategyContextWithBanner,
  setCurrentRecommendedCase,
  updateStrategyPosition
} from '@cp/src/pages/CaseRecommendation/reducer';
import {
  fetchCaseRecommendationV2NextCase,
  fetchCaseRecommendationV2PreviousCase
} from 'src/pages/CaseRecommendation/caseRecommendationActions';
import {
  StrategyType,
  AllocationDpdFilter,
  RecommendedCaseData
} from '@cp/src/pages/CaseRecommendation/types';
import { getCohortLabel } from '@cp/src/pages/CaseRecommendation/constants';

const NextPreviousCaseActionsRecommendedCSA = () => {
  const { loanId = '', customerId = '', caseReferenceId = '' } = useParams();
  const dispatch = useDispatch();

  const currentRecommendedCase = useSelector(
    (state: RootState) => state.caseRecommendation.currentRecommendedCase
  );
  const strategyContext = useSelector(
    (state: RootState) => state.caseRecommendation.strategyContext
  );
  const showEndOfNavigationModal = useSelector(
    (state: RootState) => state.caseRecommendation.showEndOfNavigationModal
  );

  const isCurrentlyOnRecommendedCase = useMemo(() => {
    if (!currentRecommendedCase?.caseReferenceId) return false;
    return (
      currentRecommendedCase.entityId === loanId &&
      currentRecommendedCase.customerReferenceId === customerId &&
      currentRecommendedCase.caseReferenceId === caseReferenceId
    );
  }, [currentRecommendedCase, loanId, customerId, caseReferenceId]);

  const hasRecommendedCaseContext = Boolean(currentRecommendedCase?.customerReferenceId);

  const hasStrategyContext = Boolean(strategyContext?.strategyType);

  const isLastCaseInStrategy = useMemo(() => {
    if (!strategyContext) return false;
    return strategyContext.currentPositionInStrategy >= strategyContext.totalCasesInStrategy;
  }, [strategyContext]);

  const handleV2NavigationSuccess = useCallback(
    (response: RecommendedCaseData) => {
      dispatch(setCurrentRecommendedCase(response));
      dispatch(
        updateStrategyPosition({
          currentPosition: response.priority,
          isEndOfNavigation: false
        })
      );
      navigateToCase(response, {
        strategyType: strategyContext?.strategyType || undefined,
        allocationDpdFilter: strategyContext?.allocationDpdFilter
      });
    },
    [strategyContext]
  );

  const onGoToPreviousCase = useCallback(() => {
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_CASE_RECOMMENDATION_NAVIGATION_PREV_CASE_CLICK);

    if (strategyContext?.strategyType) {
      dispatch(
        fetchCaseRecommendationV2PreviousCase(
          {
            strategy: strategyContext.strategyType,
            allocationDpdFilter: strategyContext.allocationDpdFilter
          },
          handleV2NavigationSuccess
        )
      );
    }
  }, [strategyContext, handleV2NavigationSuccess]);

  const onEndOfCases = () => {
    dispatch(setShowEndOfNavigationModal(true));
  };

  const onGoToNextCase = useCallback(() => {
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_CASE_RECOMMENDATION_NAVIGATION_NEXT_CASE_CLICK);

    if (isLastCaseInStrategy) {
      dispatch(setShowEndOfNavigationModal(true));
      return;
    }

    if (strategyContext?.strategyType) {
      dispatch(
        fetchCaseRecommendationV2NextCase(
          {
            strategy: strategyContext.strategyType,
            allocationDpdFilter: strategyContext.allocationDpdFilter
          },
          handleV2NavigationSuccess,
          onEndOfCases
        )
      );
    }
  }, [isLastCaseInStrategy, strategyContext, handleV2NavigationSuccess]);

  const fetchNextAndNavigate = (
    allocationDpdFilter: AllocationDpdFilter,
    strategy: StrategyType,
    overrideTotalCases?: number
  ) => {
    dispatch(
      fetchCaseRecommendationV2NextCase(
        { strategy, allocationDpdFilter },
        response => {
          dispatch(setCurrentRecommendedCase(response));
          dispatch(
            setStrategyContextWithBanner({
              strategyType: strategy,
              totalCasesInStrategy: overrideTotalCases ?? response.totalCases,
              currentPositionInStrategy: response.priority,
              allocationDpdFilter
            })
          );
          navigateToCase(response, { strategyType: strategy, allocationDpdFilter });
        },
        onEndOfCases
      )
    );
  };

  const handleFilterChange = (allocationDpdFilter: AllocationDpdFilter, strategy: StrategyType) => {
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_CASE_RECOMMENDATION_FILTER_CHANGE_V2_CLICK, {
      allocationDpdFilter,
      strategy
    });
    fetchNextAndNavigate(allocationDpdFilter, strategy);
  };

  const handleStartAgain = (allocationDpdFilter: AllocationDpdFilter, strategy: StrategyType) => {
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_CASE_RECOMMENDATION_START_AGAIN_V2_CLICK, {
      allocationDpdFilter,
      strategy
    });
    dispatch(setShowEndOfNavigationModal(false));
    fetchNextAndNavigate(allocationDpdFilter, strategy, strategyContext?.totalCasesInStrategy);
  };

  const handleChangeStrategy = (
    allocationDpdFilter: AllocationDpdFilter,
    strategy: StrategyType
  ) => {
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_CASE_RECOMMENDATION_CHANGE_STRATEGY_V2_CLICK, {
      allocationDpdFilter,
      strategy
    });
    dispatch(setShowEndOfNavigationModal(false));
    fetchNextAndNavigate(allocationDpdFilter, strategy);
  };

  const handleCloseEndOfNavigationModal = () => {
    dispatch(setShowEndOfNavigationModal(false));
  };

  if (!isCurrentlyOnRecommendedCase || !hasRecommendedCaseContext || !hasStrategyContext) {
    return null;
  }

  return (
    <>
      {strategyContext && (
        <StrategyNavigationHeader
          strategyType={strategyContext.strategyType}
          totalCasesInStrategy={strategyContext.totalCasesInStrategy}
          currentPositionInStrategy={strategyContext.currentPositionInStrategy}
          allocationDpdFilter={strategyContext.allocationDpdFilter}
          onFilterChange={handleFilterChange}
          onPrevious={onGoToPreviousCase}
          onNext={onGoToNextCase}
          isPrevDisabled={(strategyContext.currentPositionInStrategy ?? 1) === 1}
          isNextDisabled={false}
        />
      )}

      <EndOfNavigationModal
        isOpen={showEndOfNavigationModal}
        currentStrategy={strategyContext?.strategyType}
        currentDpdFilter={strategyContext?.allocationDpdFilter || AllocationDpdFilter.OVERALL}
        cohortLabel={getCohortLabel(
          strategyContext?.allocationDpdFilter || AllocationDpdFilter.OVERALL,
          strategyContext?.strategyType
        )}
        onStartAgain={handleStartAgain}
        onChangeStrategy={handleChangeStrategy}
        onClose={handleCloseEndOfNavigationModal}
      />
    </>
  );
};

export default NextPreviousCaseActionsRecommendedCSA;
